import { useEffect, useRef } from 'react';

import { LOG_EVENT_TYPE, createLogEvent } from 'src/logger/log-event';
import { LOG_LEVEL, logger } from 'src/logger/logger';
import { VIEW_EVENT } from 'src/logger/view-events';

/**
 * @param {Object} [attributes]
 * @returns {string}
 */
const serializeAttributes = (attributes) => {
    if (!attributes || Object.keys(attributes).length === 0) {
        return '';
    }

    return JSON.stringify(attributes, Object.keys(attributes).sort());
};

/**
 * Log one explicit page-view event for each mounted view identity.
 *
 * The call site owns the stable view name, route, and code locator. Optional
 * `attributes` let a view attach extra safe fields (for example
 * `attempted_pathname` on the not-found page). Callers build those values;
 * this hook only merges and emits them. Never pass query strings, hashes,
 * tokens, or other sensitive data.
 *
 * @param {Object} input
 * @param {string} input.codeLocation
 * @param {string} input.methodName
 * @param {string} input.route
 * @param {string} input.viewName
 * @param {Object} [input.attributes]
 */
export const useViewLog = ({
    codeLocation,
    methodName,
    route,
    viewName,
    attributes,
}) => {
    const lastLoggedViewRef = useRef(null);
    const attributesKey = serializeAttributes(attributes);

    useEffect(() => {
        const extraAttributes = attributesKey ? JSON.parse(attributesKey) : {};
        const viewIdentity = [
            codeLocation,
            methodName,
            route,
            viewName,
            attributesKey,
        ].join('|');

        if (lastLoggedViewRef.current === viewIdentity) {
            return;
        }

        lastLoggedViewRef.current = viewIdentity;
        logger.event(createLogEvent({
            eventType: LOG_EVENT_TYPE.VIEW,
            codeLocation,
            methodName,
            level: LOG_LEVEL.INFO,
            message: 'View displayed',
            outcome: 'displayed',
            attributes: {
                view_event: VIEW_EVENT.PAGE_VIEW,
                view_name: viewName,
                route,
                ...extraAttributes,
            },
        }));
    }, [codeLocation, methodName, route, viewName, attributesKey]);
};
